import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "./utils/api";
import { CheckCircle, AlertCircle, FileText, Shield } from "lucide-react";
import logo from "./assets/imgiconlogo.png";

const API = API_BASE_URL;

export default function SLASign() {
    const { token } = useParams();
    const [sla, setSla] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [signed, setSigned] = useState(false);
    const [alreadySigned, setAlreadySigned] = useState(false);

    // Signature form
    const [signerName, setSignerName] = useState("");
    const [signerTitle, setSignerTitle] = useState("");
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");

    useEffect(() => {
        axios.get(`${API}/api/sla/public/${token}`)
            .then(res => {
                if (res.data.alreadySigned) {
                    setAlreadySigned(true);
                } else {
                    setSla(res.data);
                }
            })
            .catch(err => {
                setError(err.response?.data?.error || "This link is invalid or has expired.");
            })
            .finally(() => setLoading(false));
    }, [token]);

    const handleSign = async (e) => {
        e.preventDefault();
        if (!signerName.trim()) return setSubmitError("Please enter your full name.");
        if (!agreed) return setSubmitError("You must agree to the terms to proceed.");
        setSubmitting(true);
        setSubmitError("");
        try {
            await axios.post(`${API}/api/sla/public/${token}/sign`, {
                signerName: signerName.trim(),
                signerTitle: signerTitle.trim(),
                agreed: true,
            });
            setSigned(true);
        } catch (err) {
            setSubmitError(err.response?.data?.error || "Failed to record signature. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-slate-500 text-sm">Loading agreement…</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
                <div className="bg-white rounded-2xl border border-red-200 shadow-sm p-8 max-w-md w-full text-center">
                    <AlertCircle size={40} className="text-red-400 mx-auto mb-4"/>
                    <h2 className="text-xl font-bold text-navy-900 mb-2">Link Invalid</h2>
                    <p className="text-slate-500 text-sm">{error}</p>
                    <p className="text-slate-400 text-xs mt-4">If you believe this is an error, contact <a href="mailto:contact@yadata.com.au" className="text-blue-600 underline">contact@yadata.com.au</a></p>
                </div>
            </div>
        );
    }

    if (alreadySigned || signed) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
                <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-10 max-w-md w-full text-center">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
                        <CheckCircle size={32} className="text-emerald-600"/>
                    </div>
                    <h2 className="text-2xl font-bold text-navy-900 mb-2">Agreement Signed</h2>
                    {signed ? (
                        <p className="text-slate-500 text-sm">
                            Thank you for signing. Confirmation copies have been sent to your email and to TinyGlobal. You can now <Link to="/login" className="text-blue-600 underline font-semibold">log in to your account</Link>.
                        </p>
                    ) : (
                        <p className="text-slate-500 text-sm">This agreement has already been signed. Please check your email for the confirmation copy.</p>
                    )}
                    <div className="mt-6">
                        <Link to="/" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">← Return to TinyGlobal.com</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 py-12 px-4">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 rounded-full overflow-hidden border border-slate-200">
                        <img src={logo} alt="TinyGlobal" className="w-full h-full object-contain"/>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400 font-semibold uppercase tracking-widest">TinyGlobal Pty Ltd</p>
                        <p className="text-sm font-bold text-navy-900">Service Level Agreement</p>
                    </div>
                </div>

                {/* Info bar */}
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 mb-6 flex items-start gap-4">
                    <FileText size={22} className="text-blue-600 shrink-0 mt-0.5"/>
                    <div>
                        <p className="font-bold text-navy-900 text-sm">{sla.tenantName} — {sla.planLabel}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Prepared for {sla.contactName} · {sla.term === "annual" ? "Annual agreement" : "Month-to-month"}
                            {sla.startDate ? ` · Starting ${sla.startDate}` : ""}
                        </p>
                    </div>
                </div>

                {/* SLA Document */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-6 overflow-hidden">
                    <div className="px-6 py-4 bg-slate-50 border-b border-slate-100">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Agreement Document — Please read in full before signing</p>
                    </div>
                    <div className="p-6">
                        <pre className="font-mono text-xs text-slate-600 whitespace-pre-wrap leading-relaxed overflow-x-auto max-h-[500px] overflow-y-auto">
                            {sla.slaText}
                        </pre>
                    </div>
                </div>

                {/* Signature Section */}
                <div className="bg-white rounded-2xl border-2 border-navy-900 shadow-sm p-6">
                    <div className="flex items-center gap-2 mb-5">
                        <Shield size={20} className="text-gold-500"/>
                        <h3 className="font-bold text-navy-900">Digital Signature</h3>
                    </div>

                    <form onSubmit={handleSign} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                    Full Name (as signature) *
                                </label>
                                <input
                                    type="text"
                                    value={signerName}
                                    onChange={e => setSignerName(e.target.value)}
                                    placeholder="Jane Smith"
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm font-semibold outline-none focus:ring-2 focus:ring-navy-900/15 focus:border-navy-900 transition-all"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                    Title / Position (optional)
                                </label>
                                <input
                                    type="text"
                                    value={signerTitle}
                                    onChange={e => setSignerTitle(e.target.value)}
                                    placeholder="CEO, Director, etc."
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/15 transition-all"
                                />
                            </div>
                        </div>

                        <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${agreed ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
                            <input
                                type="checkbox"
                                checked={agreed}
                                onChange={e => setAgreed(e.target.checked)}
                                className="mt-0.5 w-4 h-4 accent-navy-900"
                            />
                            <span className="text-sm text-slate-700">
                                I, <strong>{signerName || "___________"}</strong>, confirm I have read and understood the Service Level Agreement above, and I am authorised to sign on behalf of <strong>{sla.tenantName}</strong>. I agree to be bound by its terms.
                            </span>
                        </label>

                        {submitError && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
                                <AlertCircle size={15}/>{submitError}
                            </div>
                        )}

                        <div className="flex items-center justify-between pt-2">
                            <p className="text-[11px] text-slate-400">
                                Your IP address and timestamp are recorded for legal compliance.
                            </p>
                            <button
                                type="submit"
                                disabled={submitting || !agreed}
                                className="px-8 py-3 bg-navy-900 text-white font-bold rounded-xl hover:bg-navy-800 transition-colors disabled:opacity-40 flex items-center gap-2 shadow-lg shadow-navy-900/20"
                            >
                                {submitting ? "Signing…" : <><CheckCircle size={16}/> Sign Agreement</>}
                            </button>
                        </div>
                    </form>
                </div>

                <p className="text-center text-xs text-slate-400 mt-6">
                    Questions? Contact <a href="mailto:contact@yadata.com.au" className="text-blue-500 underline">contact@yadata.com.au</a>
                </p>
            </div>
        </div>
    );
}
